import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  icon, 
  className = "", 
  error = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => opt.value === value || opt.id === value);

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      {/* Custom Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`form-input pl-16 !py-3 !rounded-xl text-base md:text-lg font-medium bg-white border border-gray-200 shadow-sm focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all duration-150 w-full h-12 md:h-14 text-left flex items-center justify-between ${error ? 'error' : ''}`}
      >
        <div className="flex items-center">
          {/* Icon */}
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700">
            {icon}
          </span>
          
          {/* Text - Centered in remaining space */}
          <span className="ml-8 text-center flex-1 truncate">
            {selectedOption ? selectedOption.name || selectedOption.label : placeholder}
          </span>
        </div>
        
        {/* Dropdown Arrow */}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Options */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {options.map((option, index) => (
              <button
                key={option.value || option.id || index}
                type="button"
                onClick={() => handleSelect(option.value || option.id)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center ${
                  (option.value === value || option.id === value) ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                }`}
              >
                {/* Icon */}
                <span className="mr-3 text-blue-700">
                  {icon}
                </span>
                
                              {/* Text - Centered */}
              <span className="flex-1 text-center truncate">
                {option.name || option.label}
              </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdown; 